import React, { useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueDescription, systemValueLabel, systemValueOptionText } from '../../generic/systemValues/systemValueLabel'

const Technologies = () => {
  const { t_ } = useSmartTranslation()
  const [progressDefault, setProgressDefault] = useState(null)
  useEffect(() => {
    const fetchDefault = async () => {
      const progressCalculationTypes = await apiGet('progressCalculationTypes')
      const defaultValue = progressCalculationTypes.find(
        (type) => type.name === 'MANUFACTURED_PARTS'
      )
      setProgressDefault(defaultValue)
    }
    fetchDefault()
  }, [])

  const columns = [{
    name: t_('Code'),
    field: 'code',
    charLimit: 60,
    mandatory: true,
    unique: true,
    readOnly: true
  },
  {
    name: t_('Name'),
    field: 'name',
    render: (row) => systemValueLabel(t_, 'technology', row),
    renderOnlyInReadMode: true,
    charLimit: 80,
    mandatory: true,
    unique: true,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('Description'),
    field: 'description',
    charLimit: 200,
    // P10: a seeded row's description is factory text and reads translated; a customer row keeps its own.
    render: (row) => systemValueDescription(t_, 'technology', row),
    renderOnlyInReadMode: true
  },
  {
    name: t_('Asset type'),
    field: 'deviceTypes.name',
    type: 'smartMultiSelect',
    edit: {
      source: async () => {
        return await apiGet('deviceTypes')
      },
      id: 'id',
      field: 'name',
      // The chips keep the table's look; each one reads its DeviceTypeEnum constant translated.
      optionLabel: (name) => systemValueLabel(t_, 'DeviceTypeEnum', name || '')
    },
    mandatory: true,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  // SAT #3700 — every boolean column declares `defaultNewValue: false`: without it the generic table
  // initializes new-row fields to '' and Jackson coerces that to null, so Sun's DB defaults win over
  // what the checkbox shows (inspect_manufactured_parts defaults to TRUE and switched itself on).
  {
    name: t_('Part process'),
    field: 'partProcess',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Quality check'),
    field: 'generatesInspectionReport',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Inspect manufactured parts'),
    field: 'inspectManufacturedParts',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Divisible'),
    field: 'divisible',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Progress calculation'),
    field: 'progressCalculationType.name',
    render: (row) => systemValueLabel(t_, 'progress_calculation_type', row.progressCalculationType),
    renderOnlyInReadMode: true,
    filterByLabel: { options: (items) => items.map((item) => ({ value: item.name, label: systemValueLabel(t_, 'progress_calculation_type', item) })) },
    type: 'smartSelect',
    edit: {
      source: async () => {
        const progressCalculationTypes = await apiGet('progressCalculationTypes')
        return progressCalculationTypes
      },
      id: 'id',
      field: 'name',
      optionLabel: (name, items) => systemValueOptionText(t_, 'progress_calculation_type', items, name)
    },
    mandatory: true,
    defaultNewValue: progressDefault
  },
  {
    name: t_('Minimum parts'),
    field: 'minimumParts',
    numeric: true,
    defaultNewValue: 1,
    mandatory: true,
    condition: (rowData) => {
      return checkMinimumAndMaximumPartsCondition(rowData)
    }
  },
  {
    name: t_('Maximum parts'),
    field: 'maximumParts',
    numeric: true,
    defaultNewValue: 1,
    mandatory: true,
    condition: (rowData) => {
      return checkMinimumAndMaximumPartsCondition(rowData)
    }
  },
  {
    name: t_('Check all components'),
    field: 'checkAllComponents',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Check all incidents'),
    field: 'checkAllIncidents',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Check all inspections'),
    field: 'checkAllInspections',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Auto report parts'),
    field: 'autoReportParts',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Auto report resources'),
    field: 'autoReportResources',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Generate components'),
    field: 'generateComponents',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    name: t_('Sensitive'),
    field: 'sensitive',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    // Story 113 (S97, C1). `field` is CROSS-REPO CONTRACT: identical camelCase on both ends. The
    // technology is a SEED, not a gate (POL5): this value only decides the mark a NEW job node is
    // born with, and never governs a job that already exists.
    name: t_('Manage preparation'),
    field: 'managePreparation',
    type: 'boolean',
    defaultNewValue: false
  },
  {
    // Story 124 (S34). `field` is CROSS-REPO CONTRACT: identical camelCase on both ends. It stays in
    // the LAST position, where story 91's selector was, so no saved column order is disturbed (D8).
    //
    // The label reuses the `Extern` key that the JOB node checkbox already owns (D1b): same word for
    // the same concept, and zero new i18n keys. The technology is a SEED, not a gate — this value
    // only decides the mark a NEW job node is born with, and never governs a job that already exists.
    name: t_('Extern'),
    field: 'external',
    type: 'boolean',
    defaultNewValue: false
  }
  ]
  const checkMinimumAndMaximumPartsCondition = (row) => {
    let errorMsg = ''
    const min = Number(row.minimumParts)
    const max = Number(row.maximumParts)
    if (!Number.isInteger(min)) {
      errorMsg = t_('Minimum parts must be an integer')
    }
    if (min < 1) {
      errorMsg = t_('Minimum parts must be greater than or equal to 1')
    }
    if (!Number.isInteger(max)) {
      errorMsg = t_('Maximum parts must be an integer')
    }
    if (max < 1) {
      errorMsg = t_('Maximum parts must be greater than or equal to 1')
    }

    if (min > max) {
      errorMsg = t_('Minimum parts must be less than or equal to maximum parts')
    }
    return errorMsg
  }
  const mapTechnologyDeviceTypes = (row) => {
    row.technologyDeviceTypes = []
    row.deviceTypes.forEach((deviceType) => {
      row.technologyDeviceTypes.push({ deviceType: { id: deviceType.id, name: deviceType.name }, technology: { code: row.code, id: row.id, name: deviceType.name } }) // system-value: raw-ok
    })
    return row
  }

  const actions = {
    edit: {
      fetchFunction: (row) => {
        return apiPut('technologies', mapTechnologyDeviceTypes(row))
      }

    },
    create: {
      fetchFunction: (row) => {
        return apiPost('technologies', mapTechnologyDeviceTypes(row))
      }
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('technologies/' + rows[0].id, rows)
        } else {
          return apiDelete('technologiesMulti', rows)
        }
      },
      visibilityButton: (row) => {
        return !row.systemDefault
      }
    }
  }

  const fetchFunction = async (pagination) => {
    const technologies = await apiGet('technologies', pagination)
    return technologies
  }
  return (
    <SmartMateriaUITable
      tableTuneUniqueKey='technology'
      columns={columns}
      title={t_('Technologies')}
      dataFetch={fetchFunction}
      actions={actions}
      multipleSelection
      multipleOptions
      pageable
    />
  )
}

export default Technologies
